import { useEffect, useRef, useState } from "react";
import { format, subMonths } from "date-fns";
import { ReactComponent as CloseIcon } from "../../console/assets/icons/closeIcon.svg";
import { ReactComponent as RadioIcon } from "../../console/assets/icons/radioIcon.svg";
import CheckBox from "../../console/components/common/checkBox";
import DayPickerInput from "../../console/components/common/DayPickerInput";
import Button from "../../common/components/button";

/**
 * 지점 포털 상세검색
 *
 * 본점 FilterDetail 과 동일한 구성. 지점이 쓸 수 없는 항목은 뺐다.
 *  - 입금상태 : 응답에서 마스킹돼 내려온다
 *  - 발주상태 : 자기 발주 건만 보므로 무의미
 *  - 고객별 / 배송업체별 : 검색 API(/order/customer, /v1/common/delivery)가 1계층에서 차단된다
 */
const DEFAULT_ORDER_STATUS = [1, 2, 3, 4, 5, 6];

const ORDER_STATUS_LABELS = ["미확인", "접수완료", "준비완료", "배송완료", "취소", "보류"];

const DATE_OPTIONS = [
    { value: "today", label: "오늘", monthsAgo: 0 },
    { value: "month", label: "1개월", monthsAgo: 1 },
    { value: "threeMonths", label: "3개월", monthsAgo: 3 },
    { value: "sixMonths", label: "6개월", monthsAgo: 6 },
    { value: "year", label: "1년", monthsAgo: 12 },
];

type BranchFilterDetailProps = {
    filterData: {
        start_date?: string;
        end_date?: string;
        order_status?: number[];
    };
    onClose: () => void;
    onApplyFilter: (filterData: { start_date: string; end_date: string; order_status: number[] }) => void;
};

const BranchFilterDetail = ({ filterData, onClose, onApplyFilter }: BranchFilterDetailProps) => {
    const [selectedOption, setSelectedOption] = useState("");
    const [startDate, setStartDate] = useState<Date | undefined>();
    const [endDate, setEndDate] = useState<Date | undefined>();
    const [orderStatus, setOrderStatus] = useState<number[]>(DEFAULT_ORDER_STATUS);

    const filterRef = useRef<HTMLDivElement>(null);

    useEffect(() => {
        setStartDate(filterData.start_date ? new Date(filterData.start_date) : undefined);
        setEndDate(filterData.end_date ? new Date(filterData.end_date) : undefined);
        setOrderStatus(filterData.order_status?.length ? filterData.order_status : DEFAULT_ORDER_STATUS);
    }, [filterData]);

    useEffect(() => {
        const handleClickOutside = (event: MouseEvent) => {
            if (
                filterRef.current &&
                !filterRef.current.contains(event.target as Node) &&
                !(event.target as HTMLElement).closest(".rdp")
            ) {
                onClose();
            }
        };
        document.addEventListener("mousedown", handleClickOutside);
        return () => document.removeEventListener("mousedown", handleClickOutside);
    }, [onClose]);

    const handleDateSelection = (value: string, monthsAgo: number) => {
        const today = new Date();
        setSelectedOption(value);
        setStartDate(monthsAgo === 0 ? today : subMonths(today, monthsAgo));
        setEndDate(today);
    };

    const handleStartDateChange = (dateValue: Date | undefined) => {
        if (!dateValue) return;
        setStartDate(dateValue);
        setSelectedOption("");
    };

    const handleEndDateChange = (dateValue: Date | undefined) => {
        if (!dateValue) return;
        setEndDate(dateValue);
        setSelectedOption("");
    };

    const toggleOrderStatus = (status: number) => {
        setOrderStatus(prev => (prev.includes(status) ? prev.filter(s => s !== status) : [...prev, status]));
    };

    const applyFilter = () => {
        onApplyFilter({
            start_date: startDate ? format(startDate, "yyyy-MM-dd") : "",
            end_date: endDate ? format(endDate, "yyyy-MM-dd") : "",
            order_status: orderStatus.length ? orderStatus : DEFAULT_ORDER_STATUS,
        });
        onClose();
    };

    return (
        <div ref={filterRef} className="w-[560px] bg-white rounded-2xl border shadow-lg">
            <div className="flex items-center justify-between px-5 py-6">
                <p className="text-2xl font-bold">상세검색</p>
                <CloseIcon className="cursor-pointer" onClick={onClose} />
            </div>

            <div className="bg-[#F6F7FA] flex flex-col gap-3 p-5 max-h-[60vh] overflow-y-auto">
                <div className="flex items-start">
                    <p className="w-[100px] text-sm text-gray2">배송일자</p>
                    <div className="w-full flex flex-col gap-2">
                        <div className="grid grid-cols-5 h-[34px]">
                            {DATE_OPTIONS.map(option => (
                                <div
                                    key={option.value}
                                    className={`flex items-center w-full gap-2 cursor-pointer ${
                                        selectedOption === option.value ? "text-primary2" : "text-gray2"
                                    }`}
                                    onClick={() => handleDateSelection(option.value, option.monthsAgo)}
                                >
                                    {selectedOption === option.value ? (
                                        <RadioIcon className="w-8 h-8 -ml-1.5" />
                                    ) : (
                                        <div className="border-gray4 h-[19px] w-[19px] border-2 rounded-full" />
                                    )}
                                    <span className={`${selectedOption === option.value && "-ml-[7px]"}`}>{option.label}</span>
                                </div>
                            ))}
                        </div>
                        <div className="flex gap-3 relative z-50 bg-white h-[34px] px-3 border border-gray4 rounded-lg">
                            <DayPickerInput
                                twoCalendar
                                maxDate={endDate}
                                initialDate={startDate}
                                onDateChange={handleStartDateChange}
                            />
                            <DayPickerInput
                                twoCalendar
                                minDate={startDate}
                                initialDate={endDate}
                                onDateChange={handleEndDateChange}
                            />
                        </div>
                    </div>
                </div>

                <hr className="border-gray4" />

                <div className="flex items-start">
                    <p className="w-[100px] text-sm text-gray2">주문상태</p>
                    <div className="w-full text-base grid grid-cols-4 gap-2">
                        {ORDER_STATUS_LABELS.map((label, index) => (
                            <CheckBox
                                key={label}
                                label={label}
                                checked={orderStatus.includes(index + 1)}
                                onChange={() => toggleOrderStatus(index + 1)}
                            />
                        ))}
                    </div>
                </div>
            </div>

            <div className="flex items-center justify-between px-6 h-[84px] gap-2">
                <Button
                    size="L"
                    className="w-full"
                    onClick={() => {
                        setOrderStatus(DEFAULT_ORDER_STATUS);
                        setStartDate(undefined);
                        setEndDate(undefined);
                        setSelectedOption("");
                    }}
                >
                    초기화
                </Button>
                <Button size="L" className="w-[360px] flex-shrink-0" status="regist" onClick={applyFilter}>
                    적용
                </Button>
            </div>
        </div>
    );
};

export default BranchFilterDetail;
